import {
  LaunchPage,
  MaterialUiColumnsProps,
  MaterialUiTable,
  Spacer,
  usePersistMuiTableState,
} from "@qivia/ui";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import styled from "styled-components";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useGridApiRef } from "@mui/x-data-grid-premium";
import "../../i18n/i18n";
import { CarIconApi, CarIconDisplayed } from "./carIconsApi";
import {
  carIconsToReviewAsync,
  selectCarIconsToReview,
  selectCarIconsToReviewStatus,
} from "./carIconsSlice";
import { CarIconModal } from "./carIconModal";

export const CarIconsTab = () => {
  const dispatch = useAppDispatch();
  const params = useParams();
  const carIconsToReview = useAppSelector(selectCarIconsToReview);
  const carIconsToReviewStatus = useAppSelector(selectCarIconsToReviewStatus);

  const [listDisplayed, setListDisplayed] = useState<CarIconDisplayed[] | null>(
    null,
  );
  const [selectedCarIconUuid, setSelectedCarIconUuid] = useState<string | null>(
    null,
  );

  const pageName = "carIcons";
  const apiRef = useGridApiRef();
  const key = "customers-grid";

  const aggregation = {
    model: {
      brand: "size",
      model: "size",
      year: "size",
      color: "size",
    },
  };

  const carIconsDisplayed: CarIconDisplayed[] = useMemo(
    () => carIconsToReview.map((carIcon) => ({ ...carIcon, id: carIcon.uuid })),
    [carIconsToReview],
  );

  const dataGridState = usePersistMuiTableState(
    apiRef,
    key,
    pageName,
    aggregation,
  );

  const searchBarProps = {
    values: carIconsDisplayed,
    setFilterValues: setListDisplayed,
  };

  useEffect(() => {
    if (carIconsToReviewStatus === "idle") {
      void dispatch(carIconsToReviewAsync());
    }
  }, [carIconsToReviewStatus, dispatch]);

  const columnsDisplayed: {
    [index in keyof CarIconDisplayed]: MaterialUiColumnsProps;
  } = useMemo(() => {
    return {
      brand: {
        type: "string",
        width: 200,
      },
      model: {
        type: "string",
        width: 200,
      },
      year: {
        type: "number",
        width: 100,
      },
      color: {
        type: "string",
        width: 150,
      },
    };
  }, []);

  useEffect(() => {
    if (carIconsToReviewStatus === "success") {
      setListDisplayed(carIconsDisplayed);
    }
  }, [carIconsDisplayed, carIconsToReviewStatus]);

  if (params.tab !== "carIcons") {
    return null;
  }

  if (!listDisplayed) return <LaunchPage hasBorderRadius={true} />;

  return (
    <StyledContainer>
      <Spacer y={2} />
      <MaterialUiTable<keyof CarIconDisplayed, CarIconDisplayed>
        rows={listDisplayed}
        columns={columnsDisplayed}
        pageName={pageName}
        dataGridState={dataGridState}
        searchBar={searchBarProps}
        apiRef={apiRef}
        onRowClickAction={(row: Partial<CarIconApi>) =>
          setSelectedCarIconUuid(row.uuid ?? null)
        }
      />
      <CarIconModal
        carIconUuid={selectedCarIconUuid}
        onCloseModal={() => setSelectedCarIconUuid(null)}
      />
    </StyledContainer>
  );
};

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;
